/**
 * Reads `fields` for `clientId` from the cached clients dropdown, then the signed-in user's
 * client, and fetches the client once for whatever neither cached copy has.
 *
 * A cached row that lacks a field (e.g. one persisted before the column existed) counts as
 * missing, not false. A failed fetch leaves its fields undefined.
 *
 * With `fresh`, the client is always fetched and its values win over the cache, so a field
 * toggled since the dropdown was cached reads correctly. The cache is then only a fallback
 * for a failed fetch, or a field the response does not carry.
 *
 * @param {{ clients?: object[], userProfile?: object }} state the store state
 * @param {string[]} fields
 * @param {number|string|null|undefined} clientId
 * @param {{ getClientV3: Function }} clientService
 * @param {{ fresh?: boolean }} [options]
 * @returns {Promise<Object<string, *>>}
 */
export async function lookupClientFields(
  state,
  fields,
  clientId,
  clientService,
  { fresh = false } = {}
) {
  const values = {};
  if (clientId == null) return values;

  const fromDropdown = (state.clients || []).find((c) => c && c.clientId == clientId);
  const fromProfile =
    clientId == state.userProfile?.clientId ? state.userProfile?.Client : undefined;

  const missing = [];
  for (const field of fields) {
    if (fromDropdown && fromDropdown[field] !== undefined) values[field] = fromDropdown[field];
    else if (fromProfile && fromProfile[field] !== undefined) values[field] = fromProfile[field];
    else missing.push(field);
  }
  if (!fresh && !missing.length) return values;

  try {
    const result = (await clientService.getClientV3(clientId))?.result;
    for (const field of fresh ? fields : missing) {
      if (result?.[field] !== undefined) values[field] = result[field];
    }
  } catch (error) {
    console.log("--- ERROR FETCHING CLIENT FIELDS ---", fields, error?.response || error);
  }
  return values;
}
